import { useProvidersCsvDownloadQuery } from '@common/cache';
import { paths } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { debounce, upperCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useProviderTableStore,
  PersistedProviderColumn,
  initialProviderColumns,
} from '../Filters/filters.store';
import { ColumnNames } from '../types';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, Checkbox, Chip, Tooltip, Typography } from '@mui/material';
import { TruncatedTextWithConditionalTooltip } from '@components';
import { getAccountStatusColor } from '@util/user.ts';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type IProviderTableEntry =
  paths['/record/providers']['get']['responses']['200']['content']['application/json']['records'][number];

const formatCareManagerCell = ({
  assigned_cms,
  assigned_cm_count,
  isDense,
}: {
  assigned_cms: { name: string }[];
  assigned_cm_count: number;
  isDense: boolean;
}) => {
  if (assigned_cm_count === 0) {
    return <Typography variant='body2'>-</Typography>;
  }
  if (assigned_cm_count === 1 && assigned_cms.length === 1) {
    return (
      <TruncatedTextWithConditionalTooltip
        text={assigned_cms[0].name}
        variant='body2'
        width='100%'
      />
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <TruncatedTextWithConditionalTooltip
        text={assigned_cms[0].name}
        variant='body2'
        width='100%'
      />
      {!isDense && (
        <Tooltip
          title={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {assigned_cms.slice(1).map((item, idx) => (
                <Typography key={idx} variant='body2'>
                  {item.name}
                </Typography>
              ))}
            </Box>
          }
        >
          <Typography
            variant='body2'
            color='textSecondary'
          >{`+${assigned_cm_count - 1} more`}</Typography>
        </Tooltip>
      )}
    </Box>
  );
};

const buildProviderColumns = (
  isDense: boolean,
): GridColDef<IProviderTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.name}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={!!params.row.npi && `NPI: ${params.row.npi}`}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'guide_certified',
    headerName: 'GUIDE Certified',
    width: 150,
    minWidth: 150,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <Checkbox
        slotProps={{
          input: {
            'aria-label': 'GUIDE certified checkbox',
          },
        }}
        disabled
        checked={params.row.guide_certified}
      />
    ),
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.city}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={params.row.state}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'region',
    headerName: 'Region',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.region?.name ?? '-'}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'clinical_leader',
    headerName: 'Clinical Leader',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.clinical_leader?.name ?? '-'}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'supervising_physician',
    headerName: 'Supervising Physician',
    minWidth: 180,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.supervising_physician?.name ?? '-'}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.partner_name}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'practice_name',
    headerName: 'Practice',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.practice_name}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'assigned_cms',
    headerName: 'Care Managers',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params =>
      formatCareManagerCell({
        assigned_cms: params.row.assigned_cms,
        assigned_cm_count: params.row.assigned_cm_count,
        isDense,
      }),
  },
  {
    field: 'provider_specialty',
    headerName: 'Specialty',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.provider_specialty}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    minWidth: 130,
    sortable: false,
    filterable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: params => (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Chip
          sx={{
            backgroundColor: getAccountStatusColor(
              params.row.inactive ? 'Inactive' : 'Active',
            ),
            color: theme =>
              params.row.inactive
                ? 'rgba(0, 0, 0, 0.6)'
                : theme.palette.common.white,
            height: '24px',
          }}
          label={params.row.inactive ? 'Inactive' : 'Active'}
        />
      </Box>
    ),
  },
];

export const useProviderTable = () => {
  const {
    filters,
    setFilters,
    isFiltersCardOpen,
    clearStore,
    syncDraftWithAppliedFilters,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useProviderTableStore();
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const { data, isPending, mutateAsync } = useProvidersCsvDownloadQuery();

  const [searchInputValue, setSearchInputValue] = useState('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `providers`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => {
      syncDraftWithAppliedFilters();
    };
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Providers Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by as ColumnNames & undefined,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.guide_certified !== '' && {
          guide_certified: filters.guide_certified === 'true',
        }),
        ...(filters.search !== '' && { search: filters.search }),
        ...(filters.state !== '' && { state: filters.state }),
        ...(filters.status !== '' && { status: filters.status }),
        ...(filters.partner_id !== '' && { partner_id: filters.partner_id }),
        ...(filters.practice_id !== '' && { practice_id: filters.practice_id }),
        ...(filters.provider_specialty !== '' && {
          provider_specialty: filters.provider_specialty,
        }),
        ...(filters.clinical_leader_ids.length > 0 && {
          clinical_leader_ids: filters.clinical_leader_ids,
        }),
        ...(filters.supervising_physician_ids.length > 0 && {
          supervising_physician_ids: filters.supervising_physician_ids,
        }),
        ...(filters.region_ids.length > 0 && {
          region_ids: filters.region_ids,
        }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ ...filters, sort_by: 'name', sort_order: 'asc' });
      return;
    }
    setFilters({ ...filters, sort_by: field as ColumnNames, sort_order: sort });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ ...filters, limit: model.pageSize, page: model.page + 1 });
  };

  const debouncedSetSearch = useMemo(
    () =>
      debounce(
        (value: string) => setFilters({ ...filters, search: value }),
        500,
      ),
    [filters, setFilters],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSetSearch(value);
  };

  const runtimeColumns = useMemo(
    () => buildProviderColumns(density === 'dense'),
    [density],
  );
  const columns = buildGridColumns<
    IProviderTableEntry,
    PersistedProviderColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IProviderTableEntry>,
  ): PersistedProviderColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleResetAll = () => {
    clearStore();
    setSearchInputValue('');
    resetView();
  };

  return {
    filters,
    columns: columnsWithHideable,
    handleChangeOrder,
    onPageModelChange,
    handleSearchChange,
    sort_by,
    sort_order,
    handleDownload,
    isDownloading: isPending,
    searchInputValue,
    setSearchInputValue,
    isFiltersCardOpen,
    handleResetAll,
    initialSortFilters: { sort_order: 'asc', sort_by: 'name' },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    initialColumns: initialProviderColumns,
    apiRef,
  };
};
